<template>
  <section v-if="hasData" class="cv-section">
    <h3 class="cv-section__title">{{ entry.title }}</h3>

    <template v-if="entry.key === 'SUMMARY'">
      <p v-for="(t, i) in paragraphs(c.summary)" :key="i" class="cv-text">{{ t }}</p>
    </template>

    <template v-else-if="entry.key === 'SKILLS'">
      <div v-for="(g, i) in c.skills" :key="i" class="cv-skill">
        <p v-if="g.group" class="cv-skill__group">{{ g.group }}</p>
        <ul class="cv-chips">
          <li v-for="(item, j) in g.items" :key="j">{{ item }}</li>
        </ul>
      </div>
    </template>

    <template v-else-if="entry.key === 'EXPERIENCE'">
      <ul class="cv-list">
        <li v-for="(j, i) in c.experience" :key="i">
          <p class="cv-list__head">{{ j.company }}</p>
          <p v-if="j.role" class="cv-list__sub">{{ j.role }}</p>
          <p v-if="j.period" class="cv-list__when">{{ j.period }}</p>
        </li>
      </ul>
    </template>

    <template v-else-if="entry.key === 'EDUCATION'">
      <ul class="cv-list">
        <li v-for="(e, i) in c.education" :key="i">
          <p class="cv-list__head">{{ e.school }}</p>
          <p v-if="e.degree" class="cv-list__sub">{{ e.degree }}</p>
          <p v-if="e.period" class="cv-list__when">{{ e.period }}</p>
        </li>
      </ul>
    </template>

    <template v-else-if="entry.key === 'CERTIFICATIONS'">
      <ul class="cv-list">
        <li v-for="(s, i) in c.certifications" :key="i">
          <p class="cv-list__head">{{ s.title }}</p>
          <p v-if="s.issuer" class="cv-list__sub">{{ s.issuer }}</p>
          <p v-if="s.date" class="cv-list__when">{{ s.date }}</p>
        </li>
      </ul>
    </template>

    <template v-else-if="entry.key === 'LANGUAGES'">
      <ul class="cv-langs">
        <li v-for="(l, i) in c.languages" :key="i">
          {{ l.name }}<b v-if="l.level">{{ l.level }}</b>
        </li>
      </ul>
    </template>

    <template v-else-if="entry.key === 'PROJECTS'">
      <article v-for="(pr, i) in c.projects" :key="i" class="cv-project">
        <h4 class="cv-project__name">{{ pr.name }}</h4>
        <p v-if="pr.period" class="cv-project__period">{{ pr.period }}</p>
        <p v-for="(t, j) in paragraphs(pr.description)" :key="'d' + j" class="cv-text">{{ t }}</p>

        <div v-if="pr.responsibilities && pr.responsibilities.length" class="cv-project__block">
          <p class="cv-project__label">Responsibilities</p>
          <ul class="cv-bullets">
            <li v-for="(r, j) in pr.responsibilities" :key="j">{{ r }}</li>
          </ul>
        </div>

        <div v-if="pr.technologies && pr.technologies.length" class="cv-project__block">
          <p class="cv-project__label">Technologies</p>
          <ul class="cv-bullets">
            <li v-for="(t, j) in pr.technologies" :key="j">{{ t }}</li>
          </ul>
        </div>

        <div v-if="pr.extraText" class="cv-project__extra">
          <p class="cv-project__label">{{ pr.extraTitle || 'Notes' }}</p>
          <p v-for="(t, j) in paragraphs(pr.extraText)" :key="j" class="cv-text">{{ t }}</p>
        </div>
      </article>
    </template>

    <template v-else-if="entry.key === 'PERSONAL'">
      <p v-for="(t, i) in paragraphs(c.personal)" :key="i" class="cv-text">{{ t }}</p>
      <p v-if="c.hobbies" class="cv-text"><b>Hobbies:</b> {{ c.hobbies }}</p>
    </template>
  </section>
</template>

<script>
/**
 * One CV section of a candidate profile. Which section it is comes from the
 * layout entry, so the admin can reorder, rename or delete sections without any
 * change here. A section with no data renders nothing at all — no empty heading.
 */
export default {
  name: "CvSection",
  props: {
    entry: { type: Object, required: true }, // { key, title }
    c: { type: Object, required: true },     // candidate record
  },
  computed: {
    hasData() {
      const c = this.c;
      const len = (v) => Array.isArray(v) && v.length > 0;
      switch (this.entry.key) {
        case "SUMMARY": return !!c.summary;
        case "SKILLS": return len(c.skills);
        case "EXPERIENCE": return len(c.experience);
        case "EDUCATION": return len(c.education);
        case "CERTIFICATIONS": return len(c.certifications);
        case "LANGUAGES": return len(c.languages);
        case "PROJECTS": return len(c.projects);
        case "PERSONAL": return !!(c.personal || c.hobbies);
        default: return false;
      }
    },
  },
  methods: {
    paragraphs(text) {
      return String(text || "")
        .split(/\n+/)
        .map((t) => t.trim())
        .filter(Boolean);
    },
  },
};
</script>
